import { useEffect, useState } from 'react';
import { Card, Row, Col, Descriptions, Avatar, Typography, Tag, Form, Space, Alert, Button, Select, Modal, Image } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined, EnvironmentOutlined, FileTextOutlined, DollarOutlined, ClockCircleOutlined, ToolOutlined, AppstoreOutlined, SaveOutlined, CloseCircleOutlined, CheckCircleOutlined, FileImageOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import AdminSidebar from '../Components/Sidebar';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetail, userStatusUpdate } from '../redux/slices/adminSlice';
import TableComponent from '../Components/TableComponent';
import { getCheckrLogsByUserId } from '../redux/slices/settingsSlice';
import JsonView from '@uiw/react-json-view';
import { basicTheme } from '@uiw/react-json-view/basic';

const { Title } = Typography;

function ProviderDetail() {
    const [form] = Form.useForm();
    const { id } = useParams()
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(getUserDetail(id))
    }, [dispatch, id])
    const { userDetail } = useSelector((state) => state?.admin)

    const userData = userDetail?.data;
    const userDetailData = userData?.detail;

    const getInitials = () => {
        const firstName = userData?.first_name || '';
        const lastName = userData?.last_name || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    useEffect(() => {
        if (userData) {
            let showStatus = null
            if (userData?.is_verified == 1) {
                showStatus = 'Active'
            } else if (userData?.is_verified == 0) {
                showStatus = 'Inactive'
            }
            form.setFieldsValue({
                status : showStatus
            })
        }
    }, [userData, form])

    // Safely parse sub_services
    const parseSubServices = (subServices) => {
        if (!subServices) return 'N/A';

        try {
            // Check if it's already an array
            if (Array.isArray(subServices)) {
                return subServices.join(", ");
            }

            // Try to parse if it's a string
            if (typeof subServices === 'string') {
                const parsed = JSON.parse(subServices);
                if (Array.isArray(parsed)) {
                    return parsed.join(", ");
                }
                // If parsed but not an array, return as string
                return String(parsed);
            }

            return String(subServices);
        } catch (error) {
            // If parsing fails, return as string
            console.error('Error parsing sub_services:', error);
            return String(subServices);
        }
    };

    const onSubmit = async (values) => {
        setLoading(true);
        setAlertMessage("");
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            let newStatus = null
            if (values?.status == "Inactive" || values?.status == 0) {
                newStatus = 0
            } else {
                newStatus = 1
            }

            const resultAction = await dispatch(userStatusUpdate({
                id: id,
                status: newStatus
            }));

            if (userStatusUpdate.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction?.payload?.message || 'User updated successfully!');
                setAlertType('success');
                setTimeout(() => {
                    setAlertMessage('');
                }, 4000);
            } else {
                setAlertMessage(resultAction?.payload?.error || 'Failed to update user status');
                setAlertType('error');
            }
        } catch (error) {
            setAlertMessage('Failed to update user status');
            setAlertType('error');
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const columns = [
        {
            title: 'User Name',
            dataIndex: 'user_name',
            key: 'user_name',
            render: (_, record) => {
                return `${record?.user?.first_name ?? ""} ${record?.user?.last_name ?? ""}`
            }
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, record) => {
                return JSON.parse(record?.response)?.data?.object?.status ? JSON.parse(record?.response)?.data?.object?.status : record?.user?.status;
            },
        },
        // {
        //     title: 'Action',
        //     dataIndex: 'key',
        //     key: 'key',
        //     render: (_, record) => <a onClick={() => viewData(record)}>View</a>,
        // },
    ];

    const viewData = async (record) => {
        setSelectedRecord(null)
        setSelectedRecord(record);
        setIsModalOpen(true);
    }

    return (
        <>
            <AdminSidebar renderContent={
                <div style={{ padding: '0' }}>
                    {/* Provider Profile Header Card */}
                    <Card
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Row gutter={[24, 24]} align="middle">
                            <Col>
                                <Avatar
                                    size={80}
                                    style={{
                                        backgroundColor: '#CA9AE8',
                                        fontSize: '32px',
                                        fontWeight: 'bold',
                                    }}
                                    icon={<UserOutlined />}
                                >
                                    {userData && getInitials()}
                                </Avatar>
                            </Col>
                            <Col flex={1}>
                                <Title level={3} style={{ margin: 0, color: '#262626' }}>
                                    {userData ? `${userData.first_name} ${userData.last_name}` : 'Loading...'}
                                </Title>
                                <p style={{ margin: '8px 0 0 0', color: '#8c8c8c', fontSize: '16px' }}>
                                    Provider Profile Information
                                </p>
                            </Col>
                        </Row>
                    </Card>

                    {/* Personal Information Card */}
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <UserOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Personal Information
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
                            <Descriptions.Item label={<span><UserOutlined style={{ marginRight: 4 }} />Full Name</span>}>
                                {userData ? `${userData.first_name} ${userData.last_name}` : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><MailOutlined style={{ marginRight: 4 }} />Email</span>}>
                                {userData?.email || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><PhoneOutlined style={{ marginRight: 4 }} />Phone</span>}>
                                {userData?.phone || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><FileTextOutlined style={{ marginRight: 4 }} />Bio</span>}>
                                {userDetailData?.bio || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Professional Information Card */}
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <ToolOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Professional Information
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
                            <Descriptions.Item label={<span><DollarOutlined style={{ marginRight: 4 }} />Hourly Rates</span>}>
                                {userDetailData?.hourly_rates ? `$${userDetailData.hourly_rates}` : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><ClockCircleOutlined style={{ marginRight: 4 }} />Experience</span>}>
                                {userDetailData?.experience ? `${userDetailData.experience} years` : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><AppstoreOutlined style={{ marginRight: 4 }} />Specialized Service</span>}>
                                {userDetailData?.specialized_service || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><ToolOutlined style={{ marginRight: 4 }} />Sub Services</span>}>
                                {userDetailData?.sub_services ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {parseSubServices(userDetailData.sub_services).split(", ").map((service, index) => (
                                            <Tag key={index} color="blue">{service}</Tag>
                                        ))}
                                    </div>
                                ) : 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Address Information Card */}
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <HomeOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Address Information
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
                            <Descriptions.Item label={<span><HomeOutlined style={{ marginRight: 4 }} />Address</span>}>
                                {userDetailData?.address || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><EnvironmentOutlined style={{ marginRight: 4 }} />City</span>}>
                                {userDetailData?.city || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><EnvironmentOutlined style={{ marginRight: 4 }} />State</span>}>
                                {userDetailData?.state || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><EnvironmentOutlined style={{ marginRight: 4 }} />Zip Code</span>}>
                                {userDetailData?.zipcode || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <HomeOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Status
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        {alertMessage && (
                            <Alert
                                message={alertMessage}
                                type={alertType}
                                showIcon
                                icon={alertType === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                closable
                                onClose={() => setAlertMessage('')}
                                style={{
                                    marginBottom: 24,
                                    borderRadius: '8px',
                                }}
                            />
                        )}
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onSubmit}
                            autoComplete="off"
                            size="large"
                        >
                            <Form.Item
                                label={
                                    <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                        Status
                                    </span>
                                }
                                name="status"
                                rules={[
                                    { required: true, message: 'Please select status' }
                                ]}
                            >
                                <Select
                                    placeholder="Select Status"
                                    optionFilterProp="label"
                                    style={{
                                        borderRadius: '8px',
                                    }}
                                    options={[
                                        {
                                            value: '1',
                                            label: (
                                                <Space>
                                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                    Active
                                                </Space>
                                            )
                                        },
                                        {
                                            value: '0',
                                            label: (
                                                <Space>
                                                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                    Inactive
                                                </Space>
                                            )
                                        },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                                <Space size="middle">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        icon={<SaveOutlined />}
                                        loading={loading}
                                        size="large"
                                        style={{
                                            backgroundColor: '#CA9AE8',
                                            borderColor: '#CA9AE8',
                                            borderRadius: '8px',
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                            height: '42px',
                                            fontSize: '15px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Update Status
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                    <Card
                        title="Checkr Logs"
                        style={{ marginBottom: 24 }}
                    >
                        <TableComponent
                            listData={getCheckrLogsByUserId}
                            columns={columns}
                            id={id}
                            showSearch={false}
                        />
                    </Card>
                    <Modal
                        title="Checkr Log Details"
                        open={isModalOpen}
                        onCancel={() => setIsModalOpen(false)}
                        footer={null}
                    >
                        {selectedRecord && (
                            <div>
                                {selectedRecord?.user && (
                                    <p>
                                        <strong>User:</strong>{" "}
                                        {selectedRecord?.user?.first_name}{" "}
                                        {selectedRecord?.user?.last_name}
                                    </p>
                                )}
                                <div>
                                    <JsonView value={JSON.parse(selectedRecord?.response)} style={basicTheme} />
                                </div>
                            </div>
                        )}
                    </Modal>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <FileImageOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Documents
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        {userData?.userDocument?.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                {userData.userDocument.map((data, index) => (
                                    <div key={data.id || index} style={{ textAlign: 'center' }}>
                                        <p>{data?.type}</p>
                                        
                                        {/* Image Preview */}
                                        <Image
                                            src={`https://${data?.image_url}`}
                                            alt={data?.type || 'document'}
                                            width={120}
                                            height={120}
                                            style={{
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                                border: '1px solid #eee',
                                            }}
                                            preview={{
                                                mask: <EyeOutlined /> // View icon overlay
                                            }}
                                        />

                                        {/* Buttons */}
                                        <div style={{ marginTop: 8, display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            
                                            {/* View Button */}
                                            {/* <Button
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() => window.open(`https://${data?.image_url}`, '_blank')}
                                            >
                                                View
                                            </Button> */}

                                            {/* Download Button */}
                                            <Button
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={() => {
                                                    const link = document.createElement('a');
                                                    link.href = `https://${data?.image_url}`;
                                                    link.download = data?.type || 'document';
                                                    link.click();
                                                }}
                                            >
                                                Download
                                            </Button>

                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p>No documents available</p>
                        )}
                    </Card>
                </div>
            } />
        </>
    );
}

export default ProviderDetail;
